import { Fragment, useEffect, useRef, useState } from 'react';
import { getClients, getRoadmapThemes } from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { Card } from '../../detail/Card';
import { SectionTitle } from '../../detail/SectionTitle';
import { OppDrillIn } from '../opportunities/OpportunitiesDetail';

const MATURITY_LEVELS = ['Initial', 'Emerging', 'Standardised', 'Measured', 'Innovating'];

// Each cell is coloured by its own level index (not the current level), so the
// filled cells read as a colour-coded progress bar matching the maturity image.
const MATURITY_COLORS = [
  { bg: 'oklch(0.52 0.18 25)',  color: '#fff'    },  // 1 · Initial      — red
  { bg: 'oklch(0.65 0.15 50)',  color: '#fff'    },  // 2 · Emerging     — amber
  { bg: 'oklch(0.85 0.14 96)',  color: '#111214' },  // 3 · Standardised — yellow (dark text)
  { bg: 'oklch(0.60 0.10 155)', color: '#fff'    },  // 4 · Measured     — green
  { bg: 'oklch(0.52 0.14 235)', color: '#fff'    },  // 5 · Innovating   — blue
];

const MOOD_STYLE = {
  stretched: { background: 'var(--warn-soft)',   borderColor: 'var(--warn)'     },
  light:     { background: 'var(--pos-soft)',    borderColor: 'var(--pos)'      },
  new:       { background: 'var(--accent-soft)', borderColor: 'var(--accent)'   },
  steady:    { background: 'var(--paper-2)',     borderColor: 'var(--hairline)' },
};

// ─── Card grid ───────────────────────────────────────────────────────────────

function ClientCard({ client, onClick }) {
  const mood = MOOD_STYLE[client.mood] || MOOD_STYLE.steady;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="border border-ink p-3 bg-paper grid gap-0 cursor-pointer hover:bg-paper-2 transition-colors duration-100"
    >
      <div className="flex justify-between items-start gap-2">
        <div className="font-serif text-[22px] tracking-[-0.01em] leading-[1.1]">
          {client.company}
        </div>
        <span
          className="font-mono text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 border flex-shrink-0 mt-0.5"
          style={{ background: mood.background, borderColor: mood.borderColor }}
        >
          {client.mood}
        </span>
      </div>

      <div className="font-mono text-[11px] tracking-[0.04em] text-ink-3 mt-1.5">
        {client.contact}
      </div>

      <div className="border-t border-dashed border-hairline pt-3 mt-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 mb-1">
          NEXT ACTIVITY
        </div>
        <div className="text-[13px] text-ink-2">{client.next_activity}</div>
      </div>

      <div className="flex justify-between items-center mt-3">
        <span className="font-mono text-[11px] text-ink-3">{client.plan}</span>
        <span className="font-mono text-[10px] tracking-[0.1em] text-ink-4">VIEW →</span>
      </div>
    </div>
  );
}

// ─── Action form (shared by add + edit) ──────────────────────────────────────

function ActionForm({ initialText = '', initialDue = '', submitLabel = 'Add', onSave, onCancel, borderTop = true }) {
  const [text, setText] = useState(initialText);
  const [due, setDue] = useState(initialDue);
  const textRef = useRef(null);

  useEffect(() => { textRef.current?.focus(); }, []);

  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    onSave({ text: trimmed, due: due.trim() || '—' });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex gap-2 px-2.5 py-2 bg-paper-2"
      style={{ borderTop: borderTop ? '1px solid var(--hairline)' : 'none' }}
    >
      <input
        ref={textRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Action description"
        className="flex-1 min-w-0 border border-ink bg-paper px-2 py-1 text-[13px] outline-none focus:border-accent"
      />
      <input
        value={due}
        onChange={(e) => setDue(e.target.value)}
        placeholder="Due"
        className="w-[72px] border border-ink bg-paper px-2 py-1 text-[13px] font-mono outline-none focus:border-accent"
      />
      <button
        type="submit"
        className="border border-ink bg-ink text-paper font-mono text-[10px] uppercase tracking-[0.08em] px-2.5 py-1 hover:bg-ink-2 transition-colors"
      >
        {submitLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="border border-hairline font-mono text-[10px] uppercase tracking-[0.08em] px-2.5 py-1 text-ink-3 hover:text-ink transition-colors"
      >
        Cancel
      </button>
    </form>
  );
}

// ─── Drill-down ──────────────────────────────────────────────────────────────

function ClientDrillDown({ client, themes, onClose, onUpdateClient, onAddAction, onEditAction, onDeleteAction, onUpdateMaturity, onArchive, onDemoteToOpp, onDemoteToContact, onDemoteToNurture }) {
  const [addingTo, setAddingTo] = useState(null);       // theme id with open add-form
  const [editing, setEditing] = useState(null);         // { themeId, index } being edited
  const themeMap = Object.fromEntries(themes.map((t) => [t.id, t]));

  const [metaDraft, setMetaDraft] = useState({ plan: client.plan || '', end_label: client.end_label || '' });
  const metaDirty = metaDraft.plan !== (client.plan || '') || metaDraft.end_label !== (client.end_label || '');

  const [showOppCard, setShowOppCard] = useState(false);

  // All themes that have at least one action, plus any theme the user just started
  const trackedIds = themes
    .map((t) => t.id)
    .filter((id) => (client.actions?.[id]?.length ?? 0) > 0);

  const allThemeIds = themes.map((t) => t.id);
  const cols = Math.min(trackedIds.length, 3) || 1;

  return (
    <DetailShell
      eyebrow="03 · Delivery Dashboard"
      title={client.company}
      onClose={onClose}
    >

      {/* View Opportunity button */}
      {client._origin_opp && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowOppCard((v) => !v)}
            className="font-mono text-[9px] uppercase tracking-[0.06em] text-ink-4 hover:text-accent transition-colors"
          >
            {showOppCard ? 'HIDE OPPORTUNITY ↑' : 'VIEW OPPORTUNITY →'}
          </button>
        </div>
      )}

      {/* Opportunity detail (toggled) */}
      {showOppCard && client._origin_opp && (
        <OppDrillIn
          opp={client._origin_opp}
          showNav={false}
          onChange={(patch) => onUpdateClient({ _origin_opp: { ...client._origin_opp, ...patch } })}
        />
      )}

      {/* Client meta strip */}
      <Card>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', border: '1px solid var(--ink)' }}>

          {/* CONTACT — read-only */}
          <div className="p-3.5">
            <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 mb-1">CONTACT</div>
            <div className="font-serif text-[16px] leading-[1.2]">{client.contact || '—'}</div>
          </div>

          {/* PLAN — editable */}
          <div className="p-3.5" style={{ borderLeft: '1px solid var(--hairline)' }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 mb-1">PLAN</div>
            <input
              value={metaDraft.plan}
              onChange={(e) => setMetaDraft(d => ({ ...d, plan: e.target.value }))}
              className="font-serif text-[16px] leading-[1.2] bg-transparent outline-none w-full"
              style={{ borderBottom: '1px solid transparent' }}
              onFocus={(e) => (e.target.style.borderBottomColor = 'var(--hairline)')}
              onBlur={(e)  => (e.target.style.borderBottomColor = 'transparent')}
            />
          </div>

          {/* SINCE — read-only */}
          <div className="p-3.5" style={{ borderLeft: '1px solid var(--hairline)' }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 mb-1">SINCE</div>
            <div className="font-serif text-[16px] leading-[1.2]">{formatStart(client.started_at)}</div>
          </div>

          {/* HORIZON — editable */}
          <div className="p-3.5" style={{ borderLeft: '1px solid var(--hairline)' }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 mb-1">HORIZON</div>
            <input
              value={metaDraft.end_label}
              onChange={(e) => setMetaDraft(d => ({ ...d, end_label: e.target.value }))}
              className="font-serif text-[16px] leading-[1.2] bg-transparent outline-none w-full"
              style={{ borderBottom: '1px solid transparent' }}
              onFocus={(e) => (e.target.style.borderBottomColor = 'var(--hairline)')}
              onBlur={(e)  => (e.target.style.borderBottomColor = 'transparent')}
            />
          </div>

        </div>

        {client.note && client.note !== '—' && (
          <div
            className="text-[13px] text-ink-3 pl-2.5 mt-2"
            style={{ borderLeft: '2px solid var(--accent)' }}
          >
            {client.note}
          </div>
        )}
      </Card>

      {/* Maturity model */}
      <Card>
        <SectionTitle label="Maturity model" meta={`${themes.length} THEMES`} />
        <div
          className="grid"
          style={{ gridTemplateColumns: '180px repeat(5, 1fr)', border: '1px solid var(--ink)' }}
        >
          {/* Header row */}
          <div className="bg-ink text-paper font-mono text-[10px] uppercase tracking-[0.08em] p-1.5">
            THEME
          </div>
          {MATURITY_LEVELS.map((l, i) => (
            <div
              key={l}
              className="font-mono text-[9px] uppercase tracking-[0.06em] p-1.5 text-center"
              style={{
                borderLeft: '1px solid var(--paper-3)',
                background: MATURITY_COLORS[i].bg,
                color: MATURITY_COLORS[i].color,
              }}
            >
              <div style={{ opacity: 0.7, fontSize: 7, marginBottom: 1 }}>{i + 1}</div>
              {l}
            </div>
          ))}

          {/* Theme rows */}
          {themes.map((t) => {
            const level = client.maturity?.[t.id] ?? 0;
            const hasActions = (client.actions?.[t.id]?.length ?? 0) > 0;
            return (
              <Fragment key={t.id}>
                <div
                  className="text-[12px] flex items-center gap-1.5 p-2"
                  style={{ borderTop: '1px solid var(--hairline)' }}
                >
                  {hasActions && (
                    <span
                      className="w-[5px] h-[5px] rounded-full flex-shrink-0"
                      style={{ background: 'var(--accent)' }}
                    />
                  )}
                  {t.name}
                </div>
                {[0, 1, 2, 3, 4].map((k) => {
                  const isActive = k === level;
                  const mc = MATURITY_COLORS[k];
                  return (
                    <div
                      key={k}
                      role="button"
                      tabIndex={0}
                      onClick={() => onUpdateMaturity(t.id, k)}
                      onKeyDown={(e) => e.key === 'Enter' && onUpdateMaturity(t.id, k)}
                      className="text-center font-mono text-[11px] p-2 cursor-pointer"
                      title={`Set to ${MATURITY_LEVELS[k]}`}
                      style={{
                        borderTop: '1px solid var(--hairline)',
                        borderLeft: '1px solid var(--hairline)',
                        background: isActive ? mc.bg : k < level ? 'var(--paper-2)' : 'var(--paper)',
                        color: isActive ? mc.color : 'var(--ink-4)',
                        transition: 'opacity 0.1s',
                      }}
                    >
                      {k <= level ? '●' : '○'}
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">
          ● Click a cell to set maturity level · Accent dot = theme has open actions
        </div>
      </Card>

      {/* Actions per tracked theme */}
      {trackedIds.length > 0 && (
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {trackedIds.map((tid) => {
            const theme = themeMap[tid];
            if (!theme) return null;
            const acts = client.actions[tid] ?? [];
            const isAdding = addingTo === tid;
            return (
              <Card key={tid}>
                <SectionTitle label={theme.name} meta={`${acts.length} ACTIONS`} />
                <div className="border border-hairline">
                  {acts.map((a, i) => {
                    const isEditingThis = editing?.themeId === tid && editing?.index === i;
                    return isEditingThis ? (
                      <ActionForm
                        key={i}
                        initialText={a.text}
                        initialDue={a.due === '—' ? '' : a.due}
                        submitLabel="Save"
                        borderTop={i !== 0}
                        onSave={(updated) => { onEditAction(tid, i, updated); setEditing(null); }}
                        onCancel={() => setEditing(null)}
                      />
                    ) : (
                      <div
                        key={i}
                        className="group flex items-baseline gap-2 px-2.5 py-2 text-[13px]"
                        style={{ borderTop: i === 0 ? 'none' : '1px solid var(--hairline)' }}
                      >
                        <span className="flex-1 leading-[1.35]">{a.text}</span>
                        <span className="font-mono text-[11px] text-ink-4 whitespace-nowrap">{a.due}</span>
                        <button
                          onClick={() => { setEditing({ themeId: tid, index: i }); setAddingTo(null); }}
                          className="font-mono text-[9px] uppercase tracking-[0.08em] text-ink-4 opacity-0 group-hover:opacity-100 hover:text-accent transition-opacity"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDeleteAction(tid, i)}
                          className="font-mono text-[11px] text-ink-4 opacity-0 group-hover:opacity-100 hover:text-danger transition-opacity leading-none"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                  {isAdding && (
                    <ActionForm
                      onSave={(action) => { onAddAction(tid, action); setAddingTo(null); }}
                      onCancel={() => setAddingTo(null)}
                    />
                  )}
                </div>
                {!isAdding && (
                  <button
                    onClick={() => { setEditing(null); setAddingTo(tid); }}
                    className="w-full font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 border border-dashed border-ink-3 px-3.5 py-1.5 cursor-pointer mt-2.5"
                  >
                    + Add action
                  </button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add action to a theme that has none yet */}
      <Card>
        <SectionTitle label="Start tracking a theme" meta={`${allThemeIds.length - trackedIds.length} UNTRACKED`} />
        <div className="border border-hairline">
          {allThemeIds
            .filter((id) => !trackedIds.includes(id))
            .map((tid, i) => {
              const theme = themeMap[tid];
              if (!theme) return null;
              const isAdding = addingTo === tid;
              return (
                <div
                  key={tid}
                  style={{ borderTop: i === 0 ? 'none' : '1px solid var(--hairline)' }}
                >
                  {isAdding ? (
                    <div>
                      <div className="px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                        {theme.name}
                      </div>
                      <ActionForm
                        borderTop={false}
                        onSave={(action) => { onAddAction(tid, action); setAddingTo(null); }}
                        onCancel={() => setAddingTo(null)}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 px-2.5 py-2 text-[13px]">
                      <span className="text-ink-2">{theme.name}</span>
                      <button
                        onClick={() => setAddingTo(tid)}
                        className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3 border border-dashed border-ink-3 px-2.5 py-1 cursor-pointer"
                      >
                        + Add
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          {allThemeIds.filter((id) => !trackedIds.includes(id)).length === 0 && (
            <div className="px-2.5 py-2 font-mono text-[11px] text-ink-4">
              All themes tracked
            </div>
          )}
        </div>
      </Card>

      {/* Transition engagement */}
      <Card>
        <SectionTitle label="Transition engagement" meta="MOVE CLIENT" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <button
            type="button"
            onClick={() => { onArchive(client.id); onClose(); }}
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--danger)', background: 'transparent', border: '1px solid var(--danger)',
              padding: '3px 9px', cursor: 'pointer',
            }}
          >
            Archive
          </button>
          <button
            type="button"
            onClick={() => { onDemoteToOpp(client); onClose(); }}
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--ink-3)', background: 'transparent', border: '1px solid var(--ink-3)',
              padding: '3px 9px', cursor: 'pointer',
            }}
          >
            → Opportunity
          </button>
          <button
            type="button"
            onClick={() => { onDemoteToContact(client); onClose(); }}
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--ink-3)', background: 'transparent', border: '1px solid var(--ink-3)',
              padding: '3px 9px', cursor: 'pointer',
            }}
          >
            → Active Contact
          </button>
          <button
            type="button"
            onClick={() => { onDemoteToNurture(client); onClose(); }}
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--ink-3)', background: 'transparent', border: '1px solid var(--ink-3)',
              padding: '3px 9px', cursor: 'pointer',
            }}
          >
            → Nurture
          </button>
        </div>
      </Card>

      {/* Save/Cancel — sticky bottom-right, visible anywhere in the scroll area when meta fields are dirty */}
      {metaDirty && (
        <div className="sticky bottom-0 flex justify-end gap-2 z-10 pointer-events-none">
          <div className="flex gap-2 pointer-events-auto">
            <button
              type="button"
              onClick={() => onUpdateClient({ plan: metaDraft.plan, end_label: metaDraft.end_label })}
              className="border border-ink bg-ink text-paper font-mono text-[10px] uppercase tracking-[0.08em] px-4 py-1.5 hover:bg-ink-2 transition-colors cursor-pointer"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setMetaDraft({ plan: client.plan || '', end_label: client.end_label || '' })}
              className="border border-hairline bg-paper font-mono text-[10px] uppercase tracking-[0.08em] px-4 py-1.5 text-ink-3 hover:text-ink transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </DetailShell>
  );
}

// ─── Main view ───────────────────────────────────────────────────────────────

export function DeliveryView({ promotedClients = [], archivedClientIds = new Set(), onArchiveClient, onDemoteToOpp, onDemoteToContact, onDemoteToNurture }) {
  const [clients, setClients] = useState(null);
  const [themes, setThemes] = useState(null);
  const [selected, setSelected] = useState(null); // client id (string) or null
  const [error, setError] = useState(null);
  // Capture promoted clients at mount time — merging on every re-render would
  // fight with local action mutations applied after mount.
  const initialPromoted = useRef(promotedClients);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getClients(), getRoadmapThemes()])
      .then(([c, t]) => {
        if (!cancelled) {
          setClients([...c, ...initialPromoted.current]);
          setThemes(t);
        }
      })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="absolute inset-0 bg-paper flex items-center justify-center">
        <span className="font-mono text-[11px] uppercase text-danger">
          Could not load delivery data
        </span>
      </div>
    );
  }

  if (!clients || !themes) {
    return (
      <div className="absolute inset-0 bg-paper flex items-center justify-center">
        <span className="font-mono text-[11px] uppercase text-ink-3">Loading…</span>
      </div>
    );
  }

  function mutateActions(themeId, updater) {
    setClients((prev) =>
      prev.map((c) =>
        c.id !== selected ? c : {
          ...c,
          actions: {
            ...c.actions,
            [themeId]: updater(c.actions?.[themeId] ?? []),
          },
        }
      )
    );
  }

  function handleUpdateClient(patch) {
    setClients((prev) =>
      prev.map((c) => c.id !== selected ? c : { ...c, ...patch })
    );
  }

  function handleUpdateMaturity(themeId, level) {
    setClients((prev) =>
      prev.map((c) =>
        c.id !== selected ? c : {
          ...c,
          maturity: { ...c.maturity, [themeId]: level },
        }
      )
    );
  }

  function handleAddAction(themeId, action) {
    mutateActions(themeId, (acts) => [...acts, action]);
  }

  function handleEditAction(themeId, index, updated) {
    mutateActions(themeId, (acts) => acts.map((a, i) => i === index ? updated : a));
  }

  function handleDeleteAction(themeId, index) {
    mutateActions(themeId, (acts) => acts.filter((_, i) => i !== index));
  }

  const visibleClients = clients ? clients.filter((c) => !archivedClientIds.has(c.id)) : null;
  const selectedClient = selected ? (clients?.find((c) => c.id === selected) ?? null) : null;

  if (selectedClient) {
    return (
      <ClientDrillDown
        client={selectedClient}
        themes={themes}
        onClose={() => setSelected(null)}
        onUpdateClient={handleUpdateClient}
        onAddAction={handleAddAction}
        onEditAction={handleEditAction}
        onDeleteAction={handleDeleteAction}
        onUpdateMaturity={handleUpdateMaturity}
        onArchive={onArchiveClient}
        onDemoteToOpp={onDemoteToOpp}
        onDemoteToContact={onDemoteToContact}
        onDemoteToNurture={onDemoteToNurture}
      />
    );
  }

  return (
    <div className="absolute inset-0 bg-paper grid grid-rows-[auto_minmax(0,1fr)] min-h-0">
      <div className="flex items-end justify-between gap-3 px-4 pt-3 pb-2.5 border-b border-ink">
        <div>
          <div className="font-mono text-eyebrow uppercase text-ink-3">03 · Delivery Dashboard</div>
          <h1 className="font-serif text-h1-detail mt-1 leading-none">Engaged Clients</h1>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-3 self-start mt-1.5">
          {visibleClients?.length ?? 0} ACTIVE
        </span>
      </div>
      <div className="overflow-auto p-4">
        <div className="grid grid-cols-3 gap-4">
          {(visibleClients ?? []).map((c) => (
            <ClientCard key={c.id} client={c} onClick={() => setSelected(c.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function formatStart(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default DeliveryView;
